export type Lang = "en" | "zh";

export const i18n = {
  nav: {
    en: [
      { label: "Home", href: "#hero" },
      { label: "About", href: "#about" },
      { label: "Structure", href: "#structure" },
      { label: "Philosophy", href: "#philosophy" },
      { label: "Advantages", href: "#advantages" },
      { label: "Team", href: "#team" },
      { label: "Contact", href: "#contact" },
    ],
    zh: [
      { label: "首页", href: "#hero" },
      { label: "关于我们", href: "#about" },
      { label: "基金架构", href: "#structure" },
      { label: "投资理念", href: "#philosophy" },
      { label: "核心优势", href: "#advantages" },
      { label: "管理团队", href: "#team" },
      { label: "联系我们", href: "#contact" },
    ],
  },
  hero: {
    en: {
      badge: "Cayman Islands · SPC Structure",
      title: "Regent Family Fund SPC",
      subtitle: "A Professional Family Fund Focused on Global IPO & Pre-IPO Investments",
      mission: "Preserving and growing family wealth through long-term value investing",
      cta1: "Learn More",
      cta2: "Contact Us",
    },
    zh: {
      badge: "开曼群岛 · SPC 架构",
      title: "Regent Family Fund SPC",
      subtitle: "专注全球 IPO 与 Pre-IPO 的专业家族基金",
      mission: "以长期价值投资，守护家族财富稳健传承",
      cta1: "了解更多",
      cta2: "联系我们",
    },
  },
  about: {
    en: {
      tag: "About Us",
      title: "About Us",
      intro:
        "Regent Family Fund SPC is registered in the Cayman Islands, utilizing the internationally recognized Segregated Portfolio Company (SPC) structure, dedicated to global IPO and Pre-IPO investments.",
      p1: "The fund's core mission is the long-term steady appreciation of family capital, asset safety isolation, and intergenerational succession. Focusing on the Hong Kong and US stock markets — two highly liquid and well-regulated capital markets — we deeply engage in primary market quality enterprise discovery, IPO share preferential placement, and Pre-IPO strategic investment, building a focused, professional, and exclusive family capital management platform.",
      p2: "We adhere to the principles of compliance first, risk control, value investing, and long-term holding. Relying on independent governance structures, rigorous risk control systems, and professional decision-making mechanisms, we ensure transparent, standardized, and secure fund operations. Through professional investment management and global asset allocation, we are committed to becoming a trusted professional investment platform for families.",
    },
    zh: {
      tag: "About Us",
      title: "关于我们",
      intro:
        "Regent Family Fund SPC 注册于开曼群岛，采用国际金融市场广泛认可的独立投资组合公司（SPC）架构，是专注全球 IPO 与 Pre-IPO 投资领域的专业家族基金。",
      p1: "基金以家族资本长期稳健增值、资产安全隔离与代际传承为核心使命，聚焦港股、美股两大高流动性、高规范度的资本市场，深度布局一级市场优质企业挖掘、IPO 新股优先配售及 Pre-IPO 战略投资，打造专注、专业、专属的家族资本管理平台。",
      p2: "我们坚持合规优先、风险可控、价值投资、长期持有的理念，依托独立的治理结构、严格的风控体系、专业的决策机制，确保基金运作透明、规范、安全。通过专业化投资管理与全球化资产配置，致力于成为值得家族长期信赖的专业投资平台。",
    },
  },
  stats: {
    en: [
      { label: "Focus Area", value: "IPO & Pre-IPO" },
      { label: "Markets", value: "HK · US" },
      { label: "Structure", value: "SPC" },
      { label: "Domicile", value: "Cayman Islands" },
    ],
    zh: [
      { label: "专注领域", value: "IPO & Pre-IPO" },
      { label: "覆盖市场", value: "港股 · 美股" },
      { label: "基金架构", value: "SPC" },
      { label: "注册地", value: "开曼群岛" },
    ],
  },
  structure: {
    en: {
      tag: "Fund Structure",
      title: "Fund Structure",
      intro: "Utilizing the SPC framework under Cayman Islands law, featuring statutory asset segregation, risk independence, and operational flexibility",
      spcAdvantages: [
        { title: "Statutory Asset Segregation", desc: "Assets, liabilities, and risks of each portfolio are completely independent" },
        { title: "Efficient & Flexible", desc: "Supports different strategies and risk preferences" },
        { title: "Family Adaptability", desc: "Meets differentiated investment goals and generational arrangements" },
        { title: "International Recognition", desc: "Compliant with global regulatory practices for cross-border operations" },
      ],
    },
    zh: {
      tag: "Fund Structure",
      title: "基金架构",
      intro: "采用开曼群岛法律框架下的 SPC 架构，具备法定资产隔离、风险独立、运作灵活等核心优势",
      spcAdvantages: [
        { title: "法定资产隔离", desc: "各组合资产、负债、风险完全独立" },
        { title: "管理高效灵活", desc: "支持不同策略与风险偏好配置" },
        { title: "家族适配性强", desc: "满足差异化投资目标与代际安排" },
        { title: "国际认可度高", desc: "符合全球监管惯例，便于跨境运作" },
      ],
    },
  },
  portfolios: {
    en: [
      {
        name: "Prime Launch",
        focus: "HK IPO · Steady Allocation",
        description: "Focused on Hong Kong IPO, premium new share placements, and steady Pre-IPO projects, targeting enterprises with healthy cash flows, clear profit models, and sound governance.",
        sectors: ["Consumer", "Healthcare", "Advanced Manufacturing", "Digital Economy"],
        strategy: "Steady",
      },
      {
        name: "Glory Gate",
        focus: "US IPO · High Growth",
        description: "Focused on high-growth US stock sectors including technology, biotech, cloud computing, and strategic allocation of high-potential Pre-IPO enterprises, capturing long-term value in tech innovation.",
        sectors: ["AI", "Biotech", "Cloud Computing", "New Energy"],
        strategy: "Growth",
      },
    ],
    zh: [
      {
        name: "Prime Launch",
        focus: "港股 IPO · 稳健配置",
        description: "专注港股 IPO、头部优质新股配售与稳健型 Pre-IPO 项目，聚焦现金流健康、盈利模式清晰、治理规范的优质企业。",
        sectors: ["大消费", "医疗健康", "先进制造", "数字经济"],
        strategy: "稳健型",
      },
      {
        name: "Glory Gate",
        focus: "美股 IPO · 高成长",
        description: "专注美股科技、生物医药、云计算等高成长赛道 IPO 与高潜力 Pre-IPO 企业战略配置，把握科技创新长期价值。",
        sectors: ["人工智能", "生物医药", "云计算", "新能源"],
        strategy: "成长型",
      },
    ],
  },
  philosophy: {
    en: {
      tag: "Investment Philosophy",
      title: "Investment Philosophy",
      intro: "Guided by long-termism, value priority, risk control, family orientation, and professional drive",
      items: [
        { title: "Long-termism", description: "Accompanying quality enterprises over 3-5+ year cycles, pursuing sustainable compound growth" },
        { title: "Value Priority", description: "Focusing on targets with solid fundamentals, clear moats, and certain growth at reasonable valuations" },
        { title: "Risk Control", description: "Deep due diligence, diversified allocation, structural design, and process management to control downside risk" },
        { title: "Family Oriented", description: "Centered on asset safety, steady appreciation, and succession planning, balancing returns and security" },
        { title: "Professional Drive", description: "Leveraging years of primary market full-chain investment experience and cross-border institutional resources" },
      ],
      processTitle: "Investment Process",
      process: [
        { step: "01", title: "Project Screening", description: "Industry research, institutional referrals, internal scanning to build project pipeline" },
        { step: "02", title: "Deep Due Diligence", description: "Comprehensive financial, legal, business, operational, and compliance review" },
        { step: "03", title: "IC Review", description: "Independent review by directors and professionals with strict admission standards" },
        { step: "04", title: "Deal Structuring", description: "Optimizing investment terms, clarifying rights, obligations, and exit arrangements" },
        { step: "05", title: "Post-Investment", description: "Continuous tracking of enterprise operations, providing resource connections and value-added services" },
        { step: "06", title: "Exit Execution", description: "IPO, M&A, or buyback exit with timely profit settlement" },
      ],
    },
    zh: {
      tag: "Investment Philosophy",
      title: "投资理念",
      intro: "以长期主义、价值优先、风险可控、家族导向、专业驱动为核心",
      items: [
        { title: "长期主义", description: "以 3-5 年及以上周期陪伴优质企业成长，追求可持续的复利增长" },
        { title: "价值优先", description: "聚焦基本面扎实、壁垒清晰、增长确定的标的，以合理估值把握机会" },
        { title: "风险可控", description: "深度尽调、分散配置、结构设计、流程管控严控下行风险" },
        { title: "家族导向", description: "围绕资产安全、稳健增值、传承规划，兼顾收益性与安全性" },
        { title: "专业驱动", description: "依托多年一级市场全链条投资经验与跨境机构资源" },
      ],
      processTitle: "投资流程",
      process: [
        { step: "01", title: "项目筛选", description: "行业研究、机构推荐、内部扫描建立项目池" },
        { step: "02", title: "深度尽调", description: "财务、法律、业务、运营、合规全方位核查" },
        { step: "03", title: "投委会评审", description: "董事团队与专业人士独立评审，严格把控准入" },
        { step: "04", title: "交易结构设计", description: "优化投资条款，明确权利义务与退出安排" },
        { step: "05", title: "投后管理", description: "持续跟踪企业经营，提供资源对接与增值服务" },
        { step: "06", title: "退出执行", description: "IPO、并购或回购退出，及时完成收益结算" },
      ],
    },
  },
  advantages: {
    en: {
      tag: "Core Advantages",
      title: "Core Advantages",
      intro: "Six core advantages safeguarding family capital",
      items: [
        { title: "Pure Family Fund Positioning", description: "Committed to long-term stability, not short-term speculation, fully aligned with family capital objectives", icon: "crown" },
        { title: "SPC Statutory Asset Segregation", description: "Legal structure ensures asset safety with no risk contagion between portfolios", icon: "shield" },
        { title: "Dual-Core Director Team", description: "Investment experts and compliance experts working in synergy with practical experience and risk control", icon: "users" },
        { title: "Full-Chain Primary Market Capability", description: "Covering the entire IPO/Pre-IPO process from discovery, due diligence, investment to exit", icon: "layers" },
        { title: "Rich Cross-Border Resources", description: "Smooth HK and US stock channels with outstanding project acquisition and placement capabilities", icon: "globe" },
        { title: "Closed-Loop Compliance System", description: "Strict adherence to Cayman regulations, AML, information disclosure, and internal audit requirements", icon: "lock" },
      ],
    },
    zh: {
      tag: "Core Advantages",
      title: "核心优势",
      intro: "六大核心优势，为家族资本保驾护航",
      items: [
        { title: "纯正家族基金定位", description: "坚持长期稳健、非短期逐利，与家族资本目标高度一致", icon: "crown" },
        { title: "SPC 法定资产隔离", description: "从法律结构保障资产安全，风险互不传导", icon: "shield" },
        { title: "双核心董事团队", description: "投资专家+合规专家强强联合，兼具实战经验与风险把控能力", icon: "users" },
        { title: "一级市场全链条能力", description: "覆盖 IPO/Pre-IPO 从挖掘、尽调、投资到退出全流程", icon: "layers" },
        { title: "跨境机构资源丰富", description: "港美股渠道通畅，项目获取与配售能力突出", icon: "globe" },
        { title: "合规风控闭环体系", description: "严格遵守开曼监管、反洗钱、信息披露与内部审计要求", icon: "lock" },
      ],
    },
  },
  team: {
    en: {
      tag: "Leadership Team",
      title: "Leadership Team",
      intro: "A seasoned team combining investment expertise, compliance rigor, and cross-border execution capabilities",
      boardTitle: "Board of Directors",
      icTitle: "Investment Committee",
      members: [
        {
          name: "Eason",
          role: "Director · Investment Decisions",
          title: "Fund investment decisions, family asset allocation, and strategic management",
          education: "EMBA, Peking University HSBC Business School",
          expertise: [
            "Full-chain HK & US IPO and Pre-IPO investment",
            "Family wealth management & cross-border asset allocation",
            "Offshore fund governance & board management",
            "Primary market project evaluation & due diligence",
          ],
          background:
            "Extensive practical experience in Hong Kong family offices and cross-border asset management. Long-term responsibility for UHNW family global asset allocation, offshore structure establishment, Pre-IPO project evaluation, and investment execution. Possesses strong industry resource integration, market judgment, and risk management capabilities.",
          group: "board",
        },
        {
          name: "Nicole",
          role: "Director · Compliance & Risk",
          title: "Fund compliance, legal governance, and operational security management",
          education: "Legal Professional Background",
          expertise: [
            "Cayman & BVI fund regulatory frameworks",
            "AML compliance & KYC systems",
            "Cross-border investment compliance review",
            "Offshore fund full-lifecycle operational standards",
          ],
          background:
            "Years of deep expertise in cross-border finance and offshore fund compliance. Proficient in offshore fund regulatory requirements, AML compliance, information disclosure rules, capable of precisely identifying, assessing, and managing legal and operational risks. Provides solid assurance for fund compliance operations and asset security with a rigorous and meticulous approach.",
          group: "board",
        },
        {
          name: "Elvis",
          role: "Investment Committee · Member",
          title: "Investment banking, M&A, and technology venture investment",
          education: "UIBE & UC Berkeley, Financial Engineering",
          expertise: [
            "Investment banking & M&A transaction execution",
            "Technology venture investment & due diligence",
            "Biotech & embodied AI entrepreneurship",
            "Primary market IPO & Pre-IPO deal structuring",
          ],
          background:
            "Dual background in international investment banking and entrepreneurship. Previously held positions at renowned international investment banks and Chinese securities-backed investment institutions, leading M&A transactions and multiple technology project investments. Founded companies in biotech and embodied AI sectors, securing multiple rounds of funding from top-tier VCs. Brings extensive hands-on experience in primary market IPO execution, M&A structuring, and investment due diligence.",
          group: "ic",
        },
        {
          name: "Isabella",
          role: "Investment Committee · Member",
          title: "Multi-asset investment management and capital markets",
          education: "MBA, Queen Mary University of London",
          expertise: [
            "Multi-asset class investment (equities, funds, trusts, derivatives)",
            "Real estate investment & portfolio management",
            "20+ years of investment management experience",
            "Capital markets strategy & risk-return optimization",
          ],
          background:
            "Previously served at leading domestic securities firms and private equity institutions, accumulating over 20 years of investment management experience. Investment expertise spans real estate, equities, funds, trusts, futures, and other derivatives, achieving outstanding results across all asset classes. Combines deep market insight with disciplined risk management to deliver consistent long-term returns.",
          group: "ic",
        },
      ],
    },
    zh: {
      tag: "Leadership Team",
      title: "管理团队",
      intro: "资深团队融合投资专业能力、合规严谨性与跨境执行力",
      boardTitle: "基金董事",
      icTitle: "投资决策委员会",
      members: [
        {
          name: "Eason",
          role: "董事 · 投资决策",
          title: "基金投资决策、家族资产配置与战略管理",
          education: "北京大学汇丰商学院 EMBA",
          expertise: [
            "港股、美股 IPO 及 Pre-IPO 全链条投资",
            "家族财富管理与跨境资产配置",
            "离岸基金治理与董事会管理",
            "一级市场项目研判与尽职调查",
          ],
          background:
            "拥有香港家族办公室与跨境资管实战背景，长期负责高净值家族全球资产配置、离岸架构搭建、Pre-IPO 项目研判与投资落地。具备强大的行业资源整合能力、市场判断能力与风险管理能力。",
          group: "board",
        },
        {
          name: "Nicole",
          role: "董事 · 合规风控",
          title: "基金合规风控、法律治理与运营安全管理",
          education: "法学专业背景",
          expertise: [
            "开曼、BVI 基金监管法规",
            "反洗钱 AML 合规与 KYC 体系",
            "跨境投融资合规审查",
            "离岸基金全生命周期运作规范",
          ],
          background:
            "深耕跨境金融与离岸基金合规领域多年，精通离岸基金监管法规、反洗钱合规要求、信息披露规则，能够精准识别、评估与处置法律及运营风险。以严谨、细致、稳健的履职风格，为基金合规运营与资产安全提供坚实保障。",
          group: "board",
        },
        {
          name: "Elvis",
          role: "投资决策委员会 · 委员",
          title: "投资银行、收并购与科技创投",
          education: "对外经济贸易大学 & UC Berkeley 金融工程",
          expertise: [
            "投行业务与收并购交易执行",
            "科技型项目投资与尽职调查",
            "生物医药与具身智能领域创业",
            "一级市场 IPO 与 Pre-IPO 交易结构设计",
          ],
          background:
            "兼具金融投行与创业的双重背景。曾在知名国际投行和中资券商背景投资机构履职，主导参与过收并购交易和多个科技型项目的投资。在生物医药和具身智能领域均有创业公司，并获得多轮知名 VC 融资。在一级市场 IPO、收并购和投资尽调方面均有丰富的实战经验。",
          group: "ic",
        },
        {
          name: "Isabella",
          role: "投资决策委员会 · 委员",
          title: "多资产类别投资管理与资本市场",
          education: "伦敦玛丽女王学院 MBA",
          expertise: [
            "多资产类别投资（股票、基金、信托、衍生品）",
            "房地产投资与组合管理",
            "20 年以上投资管理经验",
            "资本市场策略与风险收益优化",
          ],
          background:
            "曾任职于国内知名券商和私募机构，积累超过 20 年投资管理经验。投资领域涉及房地产、股票、基金、信托、期货等衍生品，在以上领域均获得了不菲的成绩。兼具深厚的市场洞察力与严谨的风险管理能力，持续为投资者创造稳健的长期回报。",
          group: "ic",
        },
      ],
    },
  },
  wealth: {
    en: {
      tag: "Wealth Succession",
      title: "Family Wealth Succession",
      description:
        "The core of family wealth is safety, stability, and long-term succession. Regent Family Fund SPC builds on professional investment management, secured by the SPC risk isolation structure, and supported by standardized governance systems to help families achieve steady asset appreciation and smooth intergenerational transition.",
      items: [
        "Long-term value investing in IPO & Pre-IPO, providing sustainable income sources",
        "Independent portfolio structure achieving asset isolation, risk control, and flexible distribution",
        "Transparent, standardized, and institutionalized governance mechanisms enhancing family trust",
        "Long-term investment strategy achieving cross-cycle wealth preservation",
      ],
    },
    zh: {
      tag: "Wealth Succession",
      title: "家族财富传承",
      description:
        "家族财富的核心是安全、稳健与长期传承。Regent Family Fund SPC 以专业投资管理为基础，以 SPC 风险隔离架构为保障，以规范治理体系为支撑，助力家族实现资产稳健增值与代际平稳交接。",
      items: [
        "专注 IPO 与 Pre-IPO 长期价值投资，提供可持续收益来源",
        "独立投资组合架构，实现资产隔离、风险可控、分配灵活",
        "透明、规范、制度化的治理机制，增强家族信任与共识",
        "长期主义投资策略，实现跨周期财富守护",
      ],
    },
  },
  contact: {
    en: {
      tag: "Contact Us",
      title: "Contact Us",
      intro: "We uphold principles of prudent cooperation, compliant engagement, and professional service, open only to qualified investors and institutional partners",
      addresses: [
        { title: "Registered Address", value: "Regent Family Fund SPC, George Town, Grand Cayman, Cayman Islands" },
        { title: "China Office", value: "Shenzhen Trade Center, Nanshan District, Shenzhen City, Guangdong Province, CN" },
      ],
      business: { title: "Business Inquiries", value: "For fund strategy, operational model, compliance framework, and cooperation matters, please contact us via:", email: "Info@regentfamily.cn" },
      disclaimer: "We strictly comply with client privacy protection, anti-money laundering, and customer due diligence regulations, keeping all information confidential. The fund is committed to building long-term trust relationships, growing together with partners in a professional, steady, and compliant manner.",
    },
    zh: {
      tag: "Contact Us",
      title: "联系我们",
      intro: "我们坚持审慎合作、合规对接、专业服务原则，仅面向合格投资者与合作机构开放沟通",
      addresses: [
        { title: "注册地址", value: "Regent Family Fund SPC, George Town, Grand Cayman, Cayman Islands" },
        { title: "中国大陆办公室", value: "中国广东省深圳市南山区深圳贸易中心" },
      ],
      business: { title: "商务合作", value: "如需了解基金策略、运作模式、合规框架及合作事宜，请通过以下邮箱联系：", email: "Info@regentfamily.cn" },
      disclaimer: "我们将严格遵守客户隐私保护、反洗钱与客户尽职调查相关规定，对所有信息予以保密。基金致力于构建长期信任关系，以专业、稳健、合规的形象与合作伙伴共同成长。",
    },
  },
  footer: {
    en: {
      tagline: "A Professional Family Fund Focused on Global IPO & Pre-IPO",
      disclaimer: "This website is for display purposes only. Information provided is for reference only and does not constitute any investment advice, subscription invitation, or offer. Investment involves risk; investors should fully understand the risks and make independent decisions.",
      links: ["Privacy Policy", "Disclaimer", "Compliance"],
    },
    zh: {
      tagline: "专注全球 IPO 与 Pre-IPO 的专业家族基金",
      disclaimer: "本网站仅为展示使用，所提供信息仅供参考，不构成任何投资建议、认购邀请或要约。投资有风险，投资者应充分了解风险并独立做出决策。",
      links: ["隐私政策", "免责声明", "合规信息"],
    },
  },
};
